import { NextRequest, NextResponse } from 'next/server';
import {
  deleteCookie, getCookie, isBrowser, setCookie,
} from './cookiesControls';
import { LoginTokens } from './helpers/auth';

export const KEY = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  accessTokenExpiresAt: 'accessTokenExpiresAt',
  refreshTokenExpiresAt: 'refreshTokenExpiresAt',
  tokenType: 'tokenType',
  aoAuth: 'ao_auth',
  role: 'role',
};

export type StoredTokens = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  tokenType: string;
  role: string; // 'admin' | 'user'
  aoAuth: string; // '1'
};

type Context = 'client' | 'server';

/**
 * Read tokens from client cookies
 */
export function getTokens(context: Context = 'client', req?: NextRequest): StoredTokens | null {
  if (context === 'client' && !isBrowser()) return null;
  const read = (key: string) => (context === 'client' ? getCookie(key) : req?.cookies.get(key)?.value);

  const accessToken = read(KEY.accessToken);
  const refreshToken = read(KEY.refreshToken);
  const accessTokenExpiresAt = read(KEY.accessTokenExpiresAt);
  const refreshTokenExpiresAt = read(KEY.refreshTokenExpiresAt);
  const tokenType = read(KEY.tokenType);
  const role = read(KEY.role);
  const aoAuth = read(KEY.aoAuth);

  if (accessToken && refreshToken && accessTokenExpiresAt && refreshTokenExpiresAt && tokenType && role && aoAuth) {
    return {
      accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt, tokenType, role, aoAuth,
    };
  }

  return null;
}

/**
 * Set tokens to cookies
 * For client → document.cookie
 * For server → NextResponse.cookies.set()
 */
export function setTokens(
  tokens: LoginTokens,
  remember: boolean,
  context: Context = 'client',
  res?: NextResponse,
) {
  if (context === 'client' && !isBrowser()) return;

  const baseOpts = { path: '/', sameSite: 'lax' as const };

  const cookieMap: Record<string, string> = {
    [KEY.accessToken]: tokens.accessToken,
    [KEY.refreshToken]: tokens.refreshToken,
    [KEY.accessTokenExpiresAt]: tokens.accessTokenExpiresAt,
    [KEY.refreshTokenExpiresAt]: tokens.refreshTokenExpiresAt,
    [KEY.tokenType]: tokens.token_type,
    [KEY.aoAuth]: '1',
    [KEY.role]: tokens.role,
  };

  Object.entries(cookieMap).forEach(([key, value]) => {
    const expires = [KEY.refreshToken, KEY.refreshTokenExpiresAt, KEY.tokenType].includes(key)
      ? new Date(tokens.refreshTokenExpiresAt)
      : new Date(tokens.accessTokenExpiresAt);

    if (context === 'client') {
      setCookie(
        key,
        value,
        remember ? { ...baseOpts, expires } : baseOpts,
      );
    }
    if (context === 'server' && res) {
      res.cookies.set(key, value, { ...baseOpts, expires });
    }
  });
}

/**
 * Clear tokens from cookies
 */
export function clearTokens(context: Context = 'client', res?: NextResponse) {
  if (context === 'client' && !isBrowser()) return;

  const keys = Object.values(KEY);
  const baseOpts = { path: '/', sameSite: 'lax' as const };

  keys.forEach(key => {
    if (context === 'client') deleteCookie(key, baseOpts);
    if (context === 'server' && res) res.cookies.set(key, '', { ...baseOpts, maxAge: 0 });
  });
}
